export type SupportTier = 'standard' | 'urgent';
export type ProfessionalRole = 'technician' | 'teamLead';

/** Tarifas visibles para el cliente (€/h), IVA INCLUIDO (precio final). */
export const SUPPORT_CLIENT_RATES = {
  standard: 35,
  urgent: 55,
} as const satisfies Record<SupportTier, number>;

/**
 * Tarifas NETAS (sin IVA) que percibe el profesional según tier y rol.
 * Deben mantenerse en sincronía con el backend (src/config/support-pricing.ts),
 * que es la fuente de pago real. Aquí solo se usan para mostrarlas en las
 * páginas de staff.
 */
export const SUPPORT_PROFESSIONAL_RATES = {
  standard: { technician: 20.66, teamLead: 24.79 },
  urgent: { technician: 37.19, teamLead: 41.32 },
} as const satisfies Record<SupportTier, Record<ProfessionalRole, number>>;

function formatEur(eur: number): string {
  return eur.toFixed(2).replace('.', ',');
}

export function formatSupportRate(eur: number): string {
  return `${eur} €/h`;
}

/** Rango técnico–team lead de un tier, p. ej. "20,66–24,79 €/h". */
export function formatProfessionalRange(tier: SupportTier): string {
  const rates = SUPPORT_PROFESSIONAL_RATES[tier];
  return `${formatEur(rates.technician)}–${formatEur(rates.teamLead)} €/h`;
}

/** Tarifas estándar/urgente de un rol, p. ej. "20,66/37,19 €/h". */
export function formatProfessionalRoleRates(role: ProfessionalRole): string {
  return `${formatEur(SUPPORT_PROFESSIONAL_RATES.standard[role])}/${formatEur(
    SUPPORT_PROFESSIONAL_RATES.urgent[role]
  )} €/h`;
}

/** Horario en el que aplica la tarifa estándar. */
export const SUPPORT_BUSINESS_HOURS = 'lunes a viernes, de 9:00 a 18:00';

export type SupportServiceIconId = 'setup' | 'training' | 'remote' | 'urgent' | 'standard';

/** Servicios incluidos en la tarifa estándar (35 €/h). No se suman entre sí. */
export const standardSupportServices: ReadonlyArray<{
  name: string;
  description: string;
  icon: SupportServiceIconId;
}> = [
  {
    name: 'Configuración inicial',
    icon: 'setup',
    description:
      'Instalación del software y configuración del sistema adaptada a las necesidades de tu negocio.',
  },
  {
    name: 'Formación',
    icon: 'training',
    description:
      'Aprende a usar ventas, tickets, mesas, cierres de caja, informes y todas las funciones del TPV.',
  },
  {
    name: 'Asistencia remota',
    icon: 'remote',
    description:
      'Resolución de incidencias, ajustes y consultas técnicas en sesión remota con Hosty Remote.',
  },
];

export const urgentSupportService = {
  name: 'Soporte urgente',
  icon: 'urgent' as const,
  description: `Atención prioritaria fuera del horario laboral (${SUPPORT_BUSINESS_HOURS}) para incidencias críticas que afectan al funcionamiento del negocio y no pueden esperar.`,
};

export function getSupportServiceRate(tier: SupportTier): string {
  return formatSupportRate(SUPPORT_CLIENT_RATES[tier]);
}
